// Sortable column headers + optional select-all checkbox.
// asc → desc → null cycle is owned by useDataTable; this component only
// dispatches setSort and reads the current sort state for aria-sort.

import { memo } from "react";
import type { JSX, ReactNode } from "react";
import type { ColumnDef, DataTableState, SortOrder } from "./types.js";

const TH_BASE = "py-3 pr-4 font-medium text-xs text-slate-500 uppercase tracking-wide text-left";
const SORT_BTN = "inline-flex items-center gap-1 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded";

interface Props<T> {
  columns: ReadonlyArray<ColumnDef<T>>;
  state: DataTableState;
  selectable: boolean;
  visibleRowIds: readonly string[];
}

function ariaSortFor(active: boolean, order: SortOrder | undefined): "ascending" | "descending" | "none" {
  if (!active || !order) return "none";
  return order === "asc" ? "ascending" : "descending";
}

function sortGlyph(active: boolean, order: SortOrder | undefined): string {
  if (!active || !order) return "↕";
  return order === "asc" ? "↑" : "↓";
}

function HeaderCell<T>({ col, state }: { col: ColumnDef<T>; state: DataTableState }): JSX.Element {
  const key = String(col.accessorKey);
  const active = state.query.sortBy === key;
  const aSort = ariaSortFor(active, state.query.order);
  if (!col.sortable) {
    return <th scope="col" className={TH_BASE} style={{ width: col.width }}>{col.header}</th>;
  }
  return (
    <th scope="col" aria-sort={aSort} className={TH_BASE} style={{ width: col.width }}>
      <button type="button" onClick={() => state.setSort(key)} className={SORT_BTN}
        aria-label={`Sort by ${col.header}, ${aSort === "none" ? "not sorted" : aSort}`}>
        <span>{col.header}</span>
        <span aria-hidden="true" className="text-slate-400">{sortGlyph(active, state.query.order)}</span>
      </button>
    </th>
  );
}

function SelectAllCell({ state, ids }: { state: DataTableState; ids: readonly string[] }): JSX.Element {
  const all = state.selection.isAllSelected(ids);
  const some = state.selection.isSomeSelected(ids);
  return (
    <th scope="col" className="py-3 pr-4 w-8">
      <input
        type="checkbox" aria-label="Select all rows on this page"
        checked={all}
        ref={(el) => { if (el) el.indeterminate = some; }}
        onChange={() => state.selection.toggleAll(ids)}
        className="rounded border-slate-300 text-navy-700 focus:ring-navy-500"
      />
    </th>
  );
}

function HeaderRow<T>({ columns, state, selectable, visibleRowIds }: Props<T>): JSX.Element {
  return (
    <tr className="border-b border-slate-100">
      {selectable && <SelectAllCell state={state} ids={visibleRowIds} />}
      {columns.filter((c) => !c.hidden).map((c) => (
        <HeaderCell key={String(c.accessorKey)} col={c} state={state} />
      ))}
    </tr>
  );
}

const MemoHeaderRow = memo(HeaderRow) as typeof HeaderRow;

export function DataTableHeader<T>(props: Props<T> & { filterRow?: ReactNode }): JSX.Element {
  const { filterRow, ...rowProps } = props;
  return (
    <thead>
      <MemoHeaderRow {...rowProps} />
      {filterRow}
    </thead>
  );
}
